import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  image?: string;
  noIndex?: boolean;
}

/**
 * Reusable SEO component for dynamic meta tags on each page.
 * Automatically appends " | Pharmakademie" to titles.
 */
const SEO = ({
  title,
  description,
  canonical,
  type = 'website',
  publishedTime,
  modifiedTime,
  author = 'Pharmakademie',
  image,
  noIndex = false,
}: SEOProps) => {
  const fullTitle = `${title} | Pharmakademie`;
  const siteUrl = 'https://pharmakademie.com';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      {image && <meta property="og:image" content={image} />}
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="Pharmakademie" />

      {/* Article specific (for blog/topic pages) */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}

      {/* Article JSON-LD */}
      {type === 'article' && (
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: title,
          description,
          ...(image ? { image } : {}),
          ...(author ? { author: { '@type': 'Person', name: author } } : {}),
          ...(publishedTime ? { datePublished: publishedTime } : {}),
          ...(modifiedTime ? { dateModified: modifiedTime } : {}),
          ...(fullCanonical ? { mainEntityOfPage: fullCanonical } : {}),
          publisher: {
            '@type': 'Organization',
            name: 'Pharmakademie',
            url: siteUrl,
          },
        })}</script>
      )}
    </Helmet>
  );
};

export default SEO;
